import { BASIS_POINTS, evenSharesBp, type CrewTask, type PhotoKind, resolveShift, type ShiftWindow } from '@pms/shared';

import { db } from '../sync/db.js';

/**
 * Черновик сменного отчёта.
 *
 * Сохраняется после каждого шага мастера. Оператор может выйти из приложения,
 * уронить телефон, вернуться через час — и продолжить с того же места.
 * Пока отчёт не отправлен, он живёт только на устройстве.
 */

export interface DraftIncident {
  kind: 'STARTUP' | 'PURGE' | 'REPAIR' | 'TRAINING';
  durationMinutes: number | null;
  comment: string | null;
  traineeUserId: string | null;
}

export interface ReportDraft {
  /** Он же станет идентификатором отчёта — генерируется на устройстве сразу. */
  id: string;
  siteId: string;
  machineId: string;
  date: string;
  shiftNumber: number;
  participants: Array<{ userId: string; shareBp: number }>;

  /** Текущий шаг мастера: 0 — цифры, 1 — события, 2 — фото, 3 — подтверждение. */
  step: number;

  /** Прессование: годная плитка, штук по типоразмерам. */
  outputs: Record<string, number>;
  /** Прессование: брак, штук по типоразмерам. На склад не идёт — переплавляется. */
  defects: Record<string, number>;
  /** Остальные участки: граммы по видам сырья. */
  materials: Record<string, { direction: 'PRODUCED' | 'CONSUMED'; qtyG: number }>;
  batchesCount: number | null;
  recipeId: string | null;

  /** Резка и дробление: работа бригады помимо агрегата. */
  crewWork: { palletsPacked: number; palletsLoaded: number; tasks: CrewTask[] } | null;

  incidents: DraftIncident[];
  comment: string | null;
  /** Снимки с назначением: продукция и переданное рабочее место. */
  photos: Array<{ id: string; kind: PhotoKind }>;

  createdAt: string;
  updatedAt: string;
}

export interface StartDraftInput {
  siteId: string;
  machineId: string;
  date: string;
  shiftNumber: number;
  userIds: string[];
}

export function newDraft(input: StartDraftInput): ReportDraft {
  const shares = evenSharesBp(input.userIds.length);
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    siteId: input.siteId,
    machineId: input.machineId,
    date: input.date,
    shiftNumber: input.shiftNumber,
    participants: input.userIds.map((userId, index) => ({
      userId,
      shareBp: shares[index] ?? BASIS_POINTS,
    })),
    step: 0,
    outputs: {},
    defects: {},
    materials: {},
    batchesCount: null,
    recipeId: null,
    crewWork: null,
    incidents: [],
    comment: null,
    photos: [],
    createdAt: now,
    updatedAt: now,
  };
}

export async function saveDraft(draft: ReportDraft): Promise<void> {
  await db.drafts.put({ ...draft, updatedAt: new Date().toISOString() });
}

export async function loadDraft(id: string): Promise<ReportDraft | undefined> {
  return db.drafts.get(id);
}

/** Незавершённый черновик — его показываем на главном экране как «продолжить». */
export async function activeDraft(): Promise<ReportDraft | undefined> {
  const drafts = await db.drafts.orderBy('updatedAt').reverse().toArray();
  return drafts[0];
}

export async function dropDraft(id: string): Promise<void> {
  await db.drafts.delete(id);
}

/**
 * Какая смена идёт сейчас на участке. Нужно, чтобы оператор не выбирал
 * дату и номер вручную: в конце ночной смены легко ошибиться на сутки.
 */
export async function currentShiftFor(
  siteId: string,
  now = new Date(),
): Promise<{ date: string; shiftNumber: number } | null> {
  const templates = await db.shiftTemplates.where('siteId').equals(siteId).toArray();
  if (templates.length === 0) return null;

  const windows: ShiftWindow[] = templates
    .filter((template) => template.validTo === null)
    .map((template) => ({
      shiftNumber: template.shiftNumber,
      startMinute: template.startMinute,
      durationMinutes: template.durationMinutes,
    }));

  return resolveShift(now, windows);
}
